import React from 'react';
import type { ScheduleEvent } from '../../hooks/useSchedule';
import './TodayTimeline.css';

interface GapCard {
  start: string;
  end: string;
  duration: number;
  emoji: string;
  title: string;
  suggestion: string;
}

interface TodayTimelineProps {
  events: ScheduleEvent[];
  gaps: GapCard[];
  activityStatuses: Map<string, string>;
  isPast: boolean;
  isFuture: boolean;
  isLoading?: boolean;
  onEventClick: (eventId: string) => void;
  onCheckin: (itemIdStr: string, planId: number, status: string) => void;
  onAddEvent: () => void;
  onExplore: () => void;
}

function computeDuration(startRaw?: string, endRaw?: string): string | null {
  if (!startRaw || !endRaw) return null;
  try {
    const startMs = new Date(startRaw).getTime();
    const endMs = new Date(endRaw).getTime();
    if (isNaN(startMs) || isNaN(endMs)) return null;
    const diffMin = (endMs - startMs) / 60000;
    if (diffMin <= 0) return null;
    if (diffMin > 1440) return null; // Hide duration for multi-day events (>24hrs)
    if (diffMin < 60) return `${Math.round(diffMin)} min`;
    let mins = Math.round((diffMin % 60) / 5) * 5;
    let hrs = Math.floor(diffMin / 60);
    if (mins >= 60) { hrs += 1; mins = 0; } // Carry when rounding pushes to 60
    if (mins === 0) return `${hrs} hr${hrs > 1 ? 's' : ''}`;
    return `${hrs} hr ${mins} min`;
  } catch {
    return null;
  }
}

const TodayTimeline: React.FC<TodayTimelineProps> = ({
  events,
  gaps,
  activityStatuses,
  isPast,
  isFuture,
  isLoading,
  onEventClick,
  onCheckin,
  onAddEvent,
  onExplore,
}) => {
  if (isLoading && events.length === 0) {
    return (
      <div className="tt-loading">
        <div className="tt-loading-skeleton" />
        <div className="tt-loading-skeleton" />
        <div className="tt-loading-skeleton" />
      </div>
    );
  }
  if (events.length === 0) {
    return (
      <div className="tt-empty">
        <div className="tt-empty-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-sage)" strokeWidth="2" strokeLinecap="round">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
            <circle cx="12" cy="9" r="2.5"/>
          </svg>
        </div>
        <h3 className="tt-empty-title">
          {isFuture ? 'Your day is open' : isPast ? 'Nothing scheduled' : 'Nothing planned today'}
        </h3>
        <p className="tt-empty-desc">
          {isFuture ? 'What would you like to plan?' : isPast ? 'Browse around or jump back to today.' : 'Start planning or explore what others have done.'}
        </p>
        {!isPast && (
          <div className="tt-empty-actions">
            <button className="tt-empty-btn" onClick={onAddEvent}>Plan with AI</button>
            <button className="tt-empty-btn tt-empty-btn--secondary" onClick={onExplore}>See what others planned</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tt-timeline">
      {events.map((event, i) => {
        const itemId = event.itemId ? String(event.itemId) : null;
        const status = itemId
          ? (activityStatuses.get(itemId) || event.activityStatus || 'pending')
          : null;
        const isDone = status === 'completed';
        const isSkipped = status === 'skipped';
        const isCanceled = event.eventStatus === 'cancelled' || event.title.startsWith('[CANCELED]') || event.title.startsWith('[CANCELLED]');
        const displayTitle = isCanceled ? event.title.replace(/^\[CANCELL?ED\]\s*/i, '') : event.title;
        const hasPlan = !!event.planId;
        const planColor = event.planName?.toLowerCase().includes('reset') || event.planName?.toLowerCase().includes('routine')
          ? 'var(--color-sage)'
          : 'var(--accent-primary)';

        // Find gap card that belongs before this event
        const gapBefore = i > 0 ? gaps.find(g => {
          const prevEnd = events[i - 1].endTime || events[i - 1].time;
          return prevEnd && g.start && prevEnd <= g.start;
        }) : null;

        return (
          <React.Fragment key={`tt-${event.id}-${i}`}>
            {/* Gap suggestion card */}
            {gapBefore && (
              <div className="tt-gap">
                <div className="tt-gap-spacer" />
                <div className="tt-gap-line-col" />
                <div className="tt-gap-card">
                  <span className="tt-gap-label">VizDay AI</span>
                  <span className="tt-gap-text">{gapBefore.emoji} {gapBefore.suggestion}</span>
                </div>
              </div>
            )}

            <div className="tt-row">
              {/* Time column */}
              <div className="tt-time">
                <span className="tt-time-start">{event.time || ''}</span>
              </div>

              {/* Check-in circle + connector */}
              <div className="tt-circle-col">
                {hasPlan && itemId ? (
                  <div
                    className={`tt-circle ${isDone ? 'tt-circle--done' : ''} ${isSkipped ? 'tt-circle--skipped' : ''}`}
                    style={{ borderColor: planColor, background: isDone ? planColor : isSkipped ? 'var(--border)' : 'transparent' }}
                    onClick={(e) => { e.stopPropagation(); onCheckin(itemId, event.planId!, status || 'pending'); }}
                  >
                    {isDone && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>}
                    {isSkipped && <div className="tt-circle-dash" />}
                  </div>
                ) : (
                  <div className="tt-dot" />
                )}
                {i < events.length - 1 && <div className="tt-connector" />}
              </div>

              {/* Activity card */}
              <div
                className="tt-card"
                style={{ opacity: isDone ? 0.6 : isSkipped || isCanceled ? 0.35 : 1 }}
                onClick={() => onEventClick(event.id)}
              >
                <div className="tt-card-top">
                  <div className="tt-card-info">
                    {isCanceled && <span className="tt-card-canceled-badge">Canceled</span>}
                    <span className={`tt-card-title ${isSkipped || isCanceled ? 'tt-card-title--skipped' : ''}`}>{displayTitle}</span>
                    <div className="tt-card-meta">
                      {event.location && <span>{event.location}</span>}
                      {(() => {
                        const dur = computeDuration(event.timeRaw || event.time, event.endTimeRaw || event.endTime);
                        return dur ? <span>· {dur}</span> : null;
                      })()}
                    </div>
                  </div>
                  {event.planName ? (
                    <span className="tt-card-plan" style={{ color: planColor, boxShadow: `inset 0 0 0 1.5px ${planColor}` }}>
                      {event.planName}
                    </span>
                  ) : event.source && event.source !== 'vizday' ? (
                    <span className="tt-card-source">
                      {event.source === 'google' ? 'Google' : event.source === 'microsoft' ? 'Outlook' : 'Calendar'}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}

      {/* Add activity button */}
      <button className="tt-add-btn" onClick={onAddEvent}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
        Add activity
      </button>
    </div>
  );
};

export default TodayTimeline;
